/**
 * HeaderEspecificacao - Cabeçalho enxuto da Ficha (FASE 2)
 *
 * Design:
 *  - Linha 1: breadcrumb (Projetos / Nome / Embarcação #N) + ações à direita
 *  - Linha 2: chips informativos (modelo, progresso, status auto-save) — alinha à esquerda
 *
 * Reduz redundância com a coluna lateral (Resumo) que cobre cliente / custo / entrega.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { ArrowLeft, Save, FileDown, Ship, Loader2, History } from 'lucide-react';
import { formatCurrency } from './constantes';

const HeaderEspecificacao = ({
  project,
  embarcacao,
  vesselModel,
  progresso,
  custoTotal,
  podeVerCustos,
  saving,
  autoSaveStatus,
  hasChanges,
  onSave,
  onGeneratePDF,
  onOpenHistorico,
}) => {
  const navigate = useNavigate();

  const numeroEmbarcacao =
    embarcacao?.numero ||
    parseInt((window.location.pathname.split('/').pop() || '0'), 10) + 1;

  const totalEmbarcacoes = project?.embarcacoes?.length || 1;
  const projectNameShort =
    project?.name && project.name.length > 60 ? `${project.name.slice(0, 60)}…` : project?.name;

  return (
    <div className="mb-5">
      {/* Linha 1 — breadcrumb + ações */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="px-2 -ml-2"
            onClick={() => navigate('/projects')}
            data-testid="back-to-projects"
            title="Voltar para projetos"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <nav className="text-sm flex items-center gap-1.5 min-w-0 flex-wrap">
            <button
              type="button"
              onClick={() => navigate('/projects')}
              className="text-gray-500 hover:text-gray-700 hover:underline shrink-0"
            >
              Projetos
            </button>
            <span className="text-gray-300">/</span>
            <span
              className="text-gray-700 truncate max-w-md"
              title={project?.name}
            >
              {projectNameShort}
            </span>
            <span className="text-gray-300">/</span>
            <span className="font-medium text-gray-900 shrink-0 flex items-center gap-1">
              <Ship className="h-3.5 w-3.5 text-blue-600" />
              Embarcação #{String(numeroEmbarcacao).padStart(2, '0')}
              {totalEmbarcacoes > 1 && (
                <span className="text-gray-400 text-xs ml-0.5">/ {totalEmbarcacoes}</span>
              )}
            </span>
          </nav>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {hasChanges && (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-800 border-yellow-300 text-xs">
              Não salvo
            </Badge>
          )}
          {onOpenHistorico && (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenHistorico}
              data-testid="open-historico-ficha"
              title="Ver histórico de versões da ficha"
            >
              <History className="h-4 w-4 mr-1" />
              Histórico
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onGeneratePDF}
            data-testid="generate-pdf"
          >
            <FileDown className="h-4 w-4 mr-1" />
            PDF
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !hasChanges}
            size="sm"
            data-testid="save-specifications"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Salvar
          </Button>
        </div>
      </div>

      {/* Linha 2 — título + chips informativos */}
      <div className="flex flex-wrap items-end justify-between gap-3 pb-3 border-b">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 leading-tight">
            Ficha de Especificação Técnica
          </h1>
          {vesselModel && (
            <p className="text-xs text-gray-500 mt-0.5">
              Modelo:{' '}
              <span className="text-blue-700 font-medium">
                {vesselModel.nome}
              </span>
              {vesselModel.codigo && (
                <span className="text-gray-400 ml-1">({vesselModel.codigo})</span>
              )}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {autoSaveStatus && (
            <span className="text-xs text-gray-500 italic">{autoSaveStatus}</span>
          )}
          <div className="flex items-center gap-2 bg-gray-50 rounded-md px-2.5 py-1 border border-gray-200">
            <span className="text-xs font-medium text-gray-700">Progresso</span>
            <Progress value={progresso} className="w-24 h-1.5" />
            <span className="text-xs font-semibold text-gray-900 w-9 text-right">{progresso}%</span>
          </div>
          {podeVerCustos && custoTotal > 0 && (
            <div className="flex items-center gap-1.5 bg-green-50 rounded-md px-2.5 py-1 border border-green-200">
              <span className="text-xs font-medium text-green-700">Custo</span>
              <span className="text-xs font-semibold text-green-900">
                {formatCurrency(custoTotal)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeaderEspecificacao;
